import {
  Box,
  Button,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Typography,
} from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import { CheckCircleOutlineOutlined } from '@material-ui/icons'
import currency from 'currency.js'
import { first, isEmpty } from 'lodash'
import React, { useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { api, FOODSBY_BASE_URL } from '../../api/api'
import { loadCreditCardsStart } from '../../redux/modules/checkout'
import { loadOrderByIdStart } from '../../redux/modules/orderDetail'
import {
  getCurrentOrderDetailsCcLastFour,
  selectAllCreditCards,
} from '../../redux/modules/selectors'
import { normalizeErrorResponse } from '../../util/errorUtils'
import {
  pushSnowplow,
  toDeliverySchema,
  toOrderProcessSchema,
  toOrderSchema,
} from '../../util/snowplow'
import ButtonSubmit from '../common/ButtonSubmit'
import PaymentMethod from '../common/PaymentMethod'

const useStyles = makeStyles(theme => ({
  dialigTitleImage: {
    margin: '-18px 0px 0px -40px',
    paddingBottom: theme.spacing(1),
    width: 'calc(100% + 64px)',
  },
  sucessMessage: {
    color: theme.palette.success.main,
  },
}))

const DialogGratuity = ({
  order,
  showDialogGratuity,
  toggleShowGratuityDialog = Function.prototype, // Method to manually control this component. Defaults to a noop
}) => {
  const classes = useStyles()
  const ccFormRef = useRef()
  const dispatchRedux = useDispatch()
  const creditCards = useSelector(selectAllCreditCards)

  useEffect(() => {
    if (creditCards.length === 0) {
      dispatchRedux(loadCreditCardsStart())
    }
  }, [dispatchRedux, creditCards.length])
  const lastFourCcUsed = useSelector(getCurrentOrderDetailsCcLastFour)
  const lastFourCcUsedDetails = creditCards?.find?.(cc => cc.lastFour === lastFourCcUsed)

  // State
  const [open, setOpen] = useState(false)
  const [offer, setOffer] = useState()
  const [ccProfileId, setCcProfileId] = useState()
  const [isSubmittingGratuity, setIsSubmittingGratuity] = useState(false)
  const [errorCreditCard, setErrorCreditCard] = useState()
  const [isSubmittingCreditCard, setIsSubmittingCreditCard] = useState(false)
  const [success, setSuccess] = useState()
  const [error, setError] = useState()

  // consts
  const price = currency(offer?.price, {
    formatWithSymbol: true,
  }).format()
  const eligible = order?.gratuity?.eligibility?.isEligible
  const hasOffer = !isEmpty(order?.gratuity?.offers)
  const gratuityOffer = first(order?.gratuity?.offers)

  const handleIgnore = () => {
    pushSnowplow(toOrderProcessSchema('daily', 'ignored_gratuity'), [
      toOrderSchema(order?.orderId),
      toDeliverySchema(order?.deliveryDropoffId),
    ])
    handleClose()
  }
  const handleClose = () => {
    setOpen(false)
    setOffer(undefined)
    setIsSubmittingGratuity(false)
    setErrorCreditCard(undefined)
    setIsSubmittingCreditCard(false)
    showDialogGratuity && toggleShowGratuityDialog()
  }
  const handleSubmit = async () => {
    pushSnowplow(toOrderProcessSchema('daily', 'gave_gratuity', 1.0), [
      toOrderSchema(order?.orderId),
      toDeliverySchema(order?.deliveryDropoffId),
    ])
    setIsSubmittingGratuity(true)
    try {
      await api.post(`${FOODSBY_BASE_URL}/api/v1/salad/web/orders/${order.orderId}/gratuity`, {
        creditCardProfileId: ccProfileId,
        offerId: offer.id,
      })
      setSuccess(true)
      dispatchRedux(loadOrderByIdStart(order.orderId))
      if (ccFormRef.current) {
        // Create and save a new payment method
        ccFormRef.current.handleSubmit()
      }
      setTimeout(() => {
        if (open) {
          handleClose()
        }
      }, 3500)
    } catch (e) {
      setError(normalizeErrorResponse(e))
    } finally {
      setIsSubmittingGratuity(false)
    }
  }

  //snowplow event
  useEffect(() => {
    if (open) {
      pushSnowplow(toOrderProcessSchema('daily', 'viewed_gratuity'), [
        toOrderSchema(order?.orderId),
        toDeliverySchema(order?.deliveryDropoffId),
      ])
    }
  }, [open, order])

  // Show diaog if eligible and has offer
  useEffect(() => {
    if (eligible && hasOffer) {
      setOpen(true)
      setOffer(gratuityOffer)
    }
  }, [eligible, gratuityOffer, hasOffer])

  // Show offer is eligble, has offer, and external component toggles it
  useEffect(() => {
    if (!open && showDialogGratuity && eligible && hasOffer) {
      setOpen(true)
      setOffer(gratuityOffer)
    }
  }, [open, showDialogGratuity, eligible, hasOffer, gratuityOffer])

  // Default card to what was used for the order
  useEffect(() => {
    if (!isEmpty(lastFourCcUsedDetails)) {
      setCcProfileId(lastFourCcUsedDetails.ccProfileId)
    }
  }, [lastFourCcUsedDetails])

  return (
    <Dialog maxWidth="xs" onClose={handleClose} open={open}>
      <DialogTitle disableTypography>
        <img
          alt=""
          className={classes.dialigTitleImage}
          src="https://images.foodsby.com/conweb/foodsby-tip.png"
        />
        <Typography gutterBottom variant="h6">
          {success ? 'Thanks for the support' : 'Show your support'}
        </Typography>
        <Typography component="p" variant="body1">
          {success
            ? 'All proceeds go directly to the restaurant. Thanks for supporting your local restaurants.'
            : 'Happy with your service? Love your meal? Show your appreciation to the restaurant by adding gratuity.'}
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Collapse in={success}>
          <Typography align="right" className={classes.sucessMessage}>
            <CheckCircleOutlineOutlined style={{ verticalAlign: 'bottom' }} /> Payment Successful
          </Typography>
        </Collapse>
        <Collapse in={!success}>
          <Typography variant="subtitle1">PAYMENT METHODS</Typography>
          <Divider />
          <Box mb={2} mt={2}>
            <PaymentMethod
              ccFormRef={ccFormRef}
              ccProfileId={ccProfileId}
              onError={setErrorCreditCard}
              onlyExistingCards // noop
              onSuccess={Function.prototype}
              setCcProfileId={setCcProfileId}
              setSubmitting={setIsSubmittingCreditCard}
            />
          </Box>
        </Collapse>
      </DialogContent>
      <DialogActions>
        {!success && (
          <>
            <ButtonSubmit
              color="primary"
              disabled={!ccProfileId || isSubmittingCreditCard}
              onClick={handleSubmit}
              submitting={isSubmittingGratuity}
              variant="contained"
            >
              Add {price} to order
            </ButtonSubmit>
            <Button onClick={handleIgnore} variant="contained">
              No thank you
            </Button>
          </>
        )}
      </DialogActions>
      {(error || errorCreditCard) && (
        <DialogContent>
          <Typography color="error" variant="body2">
            {error || errorCreditCard}
          </Typography>
        </DialogContent>
      )}
      <Box paddingY={2} />
    </Dialog>
  )
}

export default DialogGratuity
